import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { 
  Key, Check, X, Eye, Clock, CheckCircle, XCircle, 
  Search, RefreshCw, Mail, Phone, AlertCircle
} from 'lucide-react';

/**
 * ========================================
 * PASSWORD RESET REQUESTS ADMIN
 * ========================================
 * Manage driver password reset requests
 */

const PasswordResetRequestsAdmin = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('password_reset_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setRequests(data || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  // Filter by search
  const filteredRequests = requests.filter(req => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      req.email.toLowerCase().includes(query) ||
      (req.driver_name && req.driver_name.toLowerCase().includes(query)) ||
      (req.phone && req.phone.includes(query))
    );
  });

  // Generate temporary password
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // View request details
  const handleView = (request) => {
    setSelectedRequest({
      ...request,
      tempPassword: generateTempPassword()
    });
    setShowModal(true);
  };

  // Reset password via Edge Function
  const handleResetPassword = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    setError('');

    try {
      // Call Edge Function using Supabase client
      const { data, error: fnError } = await supabase.functions.invoke('reset-driver-password', {
        body: {
          email: selectedRequest.email,
          newPassword: selectedRequest.tempPassword
        }
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to reset password');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Update request status to completed
      await supabase
        .from('password_reset_requests')
        .update({
          status: 'completed',
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      setSuccess(`Password reset successful! Send the new password via WhatsApp.`);
      
      // Keep modal open so admin can send WhatsApp
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to reset password: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Mark request as completed
  const handleMarkComplete = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    setError('');

    try {
      // Update request status to completed
      const { error: updateError } = await supabase
        .from('password_reset_requests')
        .update({
          status: 'completed',
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      if (updateError) throw updateError;

      setSuccess('Request marked as completed');
      setShowModal(false);
      fetchRequests();
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to update request');
    } finally {
      setProcessing(false);
    }
  };

  // Reject request
  const handleReject = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('password_reset_requests')
        .update({
          status: 'rejected',
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      if (updateError) throw updateError;

      setSuccess('Request rejected');
      setShowModal(false);
      fetchRequests();
    } catch (err) {
      console.error('Rejection error:', err);
      setError('Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const icons = {
      pending: <Clock size={14} />,
      completed: <CheckCircle size={14} />,
      rejected: <XCircle size={14} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    completed: requests.filter(r => r.status === 'completed').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Password Reset Requests</h1>
          <p className="text-gray-600 mt-1">Manage driver password reset requests</p>
        </div>
        <button
          onClick={fetchRequests}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
          <button onClick={() => setSuccess('')} className="float-right"><X size={18} /></button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="float-right"><X size={18} /></button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Requests</div>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-sm p-4 border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
          <div className="text-sm text-yellow-600">Pending</div>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
          <div className="text-sm text-green-600">Completed</div>
        </div>
        <div className="bg-red-50 rounded-xl shadow-sm p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
          <div className="text-sm text-red-600">Rejected</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Status Filter */}
          <div className="flex gap-2">
            {['all', 'pending', 'completed', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email, name, or phone..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Key size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests found</h3>
          <p className="text-gray-600">
            {filter === 'pending' 
              ? 'No pending password reset requests' 
              : 'No requests match your search'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        {req.driver_name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{req.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{req.phone || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={req.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(req.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleView(req)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                      >
                        <Eye size={16} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Password Reset Request</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                  {error}
                </div>
              )}

              {/* Request Info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <StatusBadge status={selectedRequest.status} />
                  <span className="text-sm text-gray-500">
                    {formatDate(selectedRequest.created_at)}
                  </span>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <span className="text-xs uppercase">Driver Name</span>
                    </div>
                    <div className="font-medium text-gray-900">
                      {selectedRequest.driver_name || 'Unknown'}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Mail size={14} />
                      <span className="text-xs uppercase">Email</span>
                    </div>
                    <div className="font-medium text-gray-900">{selectedRequest.email}</div>
                  </div>

                  {selectedRequest.phone && (
                    <div>
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <Phone size={14} />
                        <span className="text-xs uppercase">Phone</span>
                      </div>
                      <div className="font-medium text-gray-900">{selectedRequest.phone}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons (only for pending) */}
              {selectedRequest.status === 'pending' && (
                <div className="space-y-4">
                  {/* Temp Password Display */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-xs text-blue-600 uppercase font-medium mb-2">New Password</div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 font-mono text-2xl font-bold text-blue-700 bg-white px-4 py-2 rounded border">
                        {selectedRequest.tempPassword}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedRequest.tempPassword);
                          alert('Password copied!');
                        }}
                        className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                        title="Copy password"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Step 1: Reset Password */}
                  <button
                    onClick={handleResetPassword}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Key size={20} />
                        1. Reset Password
                      </>
                    )}
                  </button>

                  {/* Step 2: WhatsApp Send Password */}
                  {selectedRequest.phone && (
                    <a
                      href={`https://wa.me/${selectedRequest.phone.replace(/^0/, '27')}?text=${encodeURIComponent(
`Hi ${selectedRequest.driver_name ? selectedRequest.driver_name.split(' ')[0] : 'there'},

Your Foodline Driver App password has been reset.

📱 *App:* driver.onthemoov.co.za
📧 *Email:* ${selectedRequest.email}
🔑 *New Password:* ${selectedRequest.tempPassword}

Please login and change your password.

- Foodline Team`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-lg font-medium hover:bg-[#20bd5a] transition-colors"
                    >
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      2. Send Password via WhatsApp
                    </a>
                  )}

                  {/* Done / Close */}
                  <button
                    onClick={() => {
                      setShowModal(false);
                      fetchRequests();
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    <CheckCircle size={20} />
                    Done
                  </button>

                  {/* Reject */}
                  <button
                    onClick={handleReject}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    <X size={20} />
                    Reject Request
                  </button>
                </div>
              )}

              {/* Completed/Rejected Status */}
              {selectedRequest.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle size={24} className="mx-auto text-green-600 mb-2" />
                  <p className="text-green-700 font-medium">Password reset email was sent</p>
                  {selectedRequest.processed_at && (
                    <p className="text-green-600 text-sm mt-1">
                      Processed: {formatDate(selectedRequest.processed_at)}
                    </p>
                  )}
                </div>
              )}

              {selectedRequest.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <XCircle size={24} className="mx-auto text-red-600 mb-2" />
                  <p className="text-red-700 font-medium">Request was rejected</p>
                  {selectedRequest.processed_at && (
                    <p className="text-red-600 text-sm mt-1">
                      Processed: {formatDate(selectedRequest.processed_at)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordResetRequestsAdmin;
